import { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useAlert } from '../../../contexts/AlertContext';
import { jobApi, estimateApi, type JobSearchDataCare } from '../../../services/api';
import TopNavigation from '../../../components/feature/TopNavigation';
import { useNavigation } from '../../../hooks/useNavigation';

export default function CaregiverJobSearchPage() {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const { goBack, navigateTo } = useNavigation();

  const [selectedFilter, setSelectedFilter] = useState<0 | 1 | 3>(0); // careWhat 기준
  const [jobPostings, setJobPostings] = useState<JobSearchDataCare[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const [sentQuoteCount, setSentQuoteCount] = useState(0);
  const [isFabOpen, setIsFabOpen] = useState(false);
  // 초기 로드
  useEffect(() => {
    loadJobPostings(1, selectedFilter);
  }, [selectedFilter]);

  useEffect(() => {
    loadSentQuoteCount();
  }, []);

  const loadSentQuoteCount = async () => {
    if (!user) return;
    try {
      const response = await estimateApi.getSentQuotes({
        memberNo: user.memberNo,
        apiKey: user.apiKey,
        page: 1,
        perPageNum: 1
      });
      setSentQuoteCount(response.pagingList.totalCnt);
    } catch (error) {
      // 건수 조회 실패 시 무시
    }
  };

  // 일감 목록 로드
  const loadJobPostings = async (pageNum: number, careWhat: number) => {
    if (!user) return;

    setIsLoading(true);
    try {
      const response = await jobApi.search({
        memberNo: user.memberNo,
        apiKey: user.apiKey,
        page: pageNum,
        perPageNum: 20
      });

      // careWhat으로 필터링 (가족간병 제외)
      const filteredList = response.careList.filter(job => {
        if (job.careWhat === 2) return false; // 가족간병 제외
        return job.careWhat === careWhat;
      });

      if (pageNum === 1) {
        setJobPostings(filteredList);
      } else {
        setJobPostings(prev => [...prev, ...filteredList]);
      }

      setPage(pageNum);
      setHasMore(response.pagingList.totalCnt > pageNum * 20);

    } catch (error) {
      console.error('[일감 찾기] 로드 실패:', error);
      showAlert(error instanceof Error ? error.message : '일감 목록을 불러오는데 실패했습니다.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackClick = () => {
    goBack();
    //navigate("/");
  };

  const handleJobClick = (jobId: number) => {
    navigateTo(`/caregiver/job-detail/${jobId}`);
  };

  // careWhat 값에 따른 라벨
  const getCareWhatLabel = (careWhat: number) => {
    switch (careWhat) {
      case 0: return '단순간병';
      case 1: return '전문간병';
      case 3: return '병원동행';
      default: return '간병';
    }
  };

  // careKind 값에 따른 라벨
  const getCareKindLabel = (careKind: number) => {
    switch (careKind) {
      case 0: return '기간제';
      case 1: return '시간제';
      case 2: return '24시간제';
      default: return '기간제';
    }
  };

  // matchType 값에 따른 라벨
  const getMatchTypeLabel = (matchType: string) => {
    return matchType === 'F' ? '정찰제' : '입찰제';
  };

  // 간병 기간 계산
  const calculateDuration = (startDate: string, endDate: string, careKind: number) => {
    const normalizedStart = startDate.replace(/\./g, '-');
    const normalizedEnd = endDate.replace(/\./g, '-');
    const start = new Date(normalizedStart);
    const end = new Date(normalizedEnd);
    const diffTime = end.getTime() - start.getTime();
    const rawDays = Math.round(diffTime / (1000 * 60 * 60 * 24)) + 1; // 당일 포함
    const days = careKind === 2 ? rawDays - 1 : rawDays; // 24시간제는 박 수
    return days + '일';
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <TopNavigation
        title="구인공고"
        showBackButton
        onBackClick={handleBackClick}
      />

      {/* 필터 탭 */}
      <div className="bg-white mt-16 px-2 py-2.5 border-b border-gray-100">
        <div className="flex rounded-xl p-1 gap-1" style={{ background: 'var(--color-background-secondary, #f0f0f0)' }}>
          {([
            { value: 0, label: '단순간병' },
            { value: 1, label: '전문간병' },
            { value: 3, label: '병원동행' },
          ] as { value: 0 | 1 | 3; label: string }[]).map((tab) => (
            <button
              key={tab.value}
              onClick={() => setSelectedFilter(tab.value)}
              className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${selectedFilter === tab.value
                ? 'bg-purple-600 text-white shadow-sm'
                : 'text-gray-500 bg-transparent'
                }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* 로딩 */}
      {isLoading && jobPostings.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <p className="text-gray-500 text-sm">일감을 불러오는 중...</p>
          </div>
        </div>
      )}

      {/* 빈 목록 */}
      {!isLoading && jobPostings.length === 0 && (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <i className="ri-inbox-line text-6xl text-gray-300 mb-3"></i>
            <p className="text-gray-500 text-sm">등록된 공고가 없습니다.</p>
          </div>
        </div>
      )}

      {/* 공고 목록 */}
      {jobPostings.length > 0 && (
        <div className="flex-1 overflow-y-auto px-2 py-3">

          {/* 건수 표시 */}
          <p className="text-xs text-gray-400 mb-3">
            총 {jobPostings.length}건의 공고
          </p>

          <div className="space-y-3">
            {jobPostings.map((job) => {
              const isFixed = job.matchType === 'F';

              // 근무 시간 계산
              const calcHours = () => {
                if (!job.startCareTime || !job.endCareTime) return null;
                const [sh, sm] = job.startCareTime.split(':').map(Number);
                const [eh, em] = job.endCareTime.split(':').map(Number);
                const hours = Math.round(((eh * 60 + em) - (sh * 60 + sm)) / 60);
                return hours > 0 ? hours : null;
              };
              const workHours = calcHours();

              // careKind 뱃지 색상
              const getCareKindStyle = (careKind: number) => {
                if (careKind === 2) return { background: '#FAECE7', color: '#993C1D' };
                return { background: '#E6F1FB', color: '#185FA5' };
              };

              return (
                <div
                  key={job.careNo}
                  className="bg-white rounded-md overflow-hidden border border-gray-100 cursor-pointer active:opacity-90 transition-opacity"
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                  onClick={() => handleJobClick(job.careNo)}
                >
                  {/* 상단 컬러 바 */}
                  <div className="h-1 w-full" style={{
                    background: isFixed ? '#F97316' : '#7966FF'
                  }}></div>

                  <div className="p-4">
                    {/* 뱃지 + 금액 */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex gap-1.5 flex-wrap flex-1 mr-2">
                        <span className="text-[11px] px-2 py-0.5 rounded-md font-medium"
                          style={{ background: '#EEEDFE', color: '#3C3489' }}>
                          {getCareWhatLabel(job.careWhat)}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-md font-medium"
                          style={getCareKindStyle(job.careKind)}>
                          {getCareKindLabel(job.careKind)}
                        </span>
                        <span className="text-[11px] px-2 py-0.5 rounded-md font-medium"
                          style={isFixed
                            ? { background: '#EAF3DE', color: '#3B6D11' }
                            : { background: '#FAEEDA', color: '#854F0B' }}>
                          {getMatchTypeLabel(job.matchType)}
                        </span>
                      </div>
                      <div className="text-right flex-shrink-0">
                        {isFixed ? (
                          <>
                            <p className="text-base font-semibold" style={{ color: '#7966FF' }}>
                              {job.totalMoney.toLocaleString()}원
                            </p>
                            {job.oneDayMoney > 0 && (
                              <p className="text-[11px] text-gray-400 mt-0.5">
                                {job.careKind === 2 ? '일당' : '시급'} {job.oneDayMoney.toLocaleString()}원
                              </p>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-sm font-medium" style={{ color: '#854F0B' }}>견적 제출</p>
                            <p className="text-[11px] text-gray-400 mt-0.5">금액 직접 제안</p>
                          </>
                        )}
                      </div>
                    </div>

                    {/* 위치/날짜/시간 정보 */}
                    <div className="space-y-1.5 mb-3">
                      <div className="flex items-start gap-1.5">
                        <i className="ri-map-pin-line text-gray-400 text-sm flex-shrink-0 mt-0.5"></i>
                        <span className="text-sm text-gray-700 leading-snug">
                          {job.careArea}
                          {job.careAreaDetail && (
                            <span className="text-gray-400"> · {job.careAreaDetail}</span>
                          )}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-calendar-line text-gray-400 text-sm flex-shrink-0"></i>
                        <span className="text-sm text-gray-700">
                          {job.startDate} ~ {job.endDate}
                        </span>
                        <span className="text-[11px] px-1.5 py-0.5 rounded text-gray-500"
                          style={{ background: 'var(--color-background-secondary, #f0f0f0)' }}>
                          {calculateDuration(job.startDate, job.endDate, job.careKind)}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5">
                        <i className="ri-time-line text-gray-400 text-sm flex-shrink-0"></i>
                        <span className="text-sm text-gray-700">
                          {job.startCareTime} ~ {job.endCareTime}
                        </span>
                        {workHours && job.careKind !== 2 && (
                          <span className="text-[11px] text-gray-400">({workHours}시간)</span>
                        )}
                      </div>
                    </div>

                    {/* 지원 버튼 */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleJobClick(job.careNo);
                      }}
                      className="w-full py-2.5 rounded-lg text-sm font-medium text-white transition-opacity active:opacity-80"
                      style={{ background: isFixed ? '#F97316' : '#7966FF' }}
                    >
                      {isFixed ? '지원하기' : '견적 제출하기'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 더보기 */}
          {hasMore && !isLoading && (
            <div className="mt-4 mb-4">
              <button
                onClick={() => loadJobPostings(page + 1, selectedFilter)}
                className="w-full py-3 bg-white border border-gray-200 rounded-xl text-sm text-gray-600 font-medium"
              >
                더보기
              </button>
            </div>
          )}

          {/* 로딩 더보기 */}
          {isLoading && jobPostings.length > 0 && (
            <div className="py-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          )}
        </div>
      )}
      {/* ⭐ 보낸견적 FAB */}
      <>
        {/* 배경 딤 */}
        {isFabOpen && (
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsFabOpen(false)}
          ></div>
        )}

        <div className="fixed bottom-10 right-4 z-50 flex flex-col items-end gap-2">
          {/* 펼쳐진 메뉴 */}
          {isFabOpen && (
            <div
              className="flex items-center gap-2 mb-1"
              onClick={() => {
                setIsFabOpen(false);
                navigateTo('/profile/sent-quotes');
              }}
            >
              <div className="bg-white border border-gray-100 rounded-lg px-3 py-2 cursor-pointer"
                style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.12)' }}>
                <p className="text-sm font-medium text-gray-800 whitespace-nowrap">보낸 견적 확인</p>
                {sentQuoteCount > 0 && (
                  <p className="text-xs text-purple-600 mt-0.5">총 {sentQuoteCount}건</p>
                )}
              </div>
              <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ background: '#7966FF', boxShadow: '0 2px 8px rgba(121,102,255,0.4)' }}>
                <i className="ri-file-list-line text-white text-base"></i>
              </div>
            </div>
          )}

          {/* 메인 FAB 버튼 */}
          <button
            onClick={() => setIsFabOpen(prev => !prev)}
            className="w-14 h-14 rounded-full flex items-center justify-center text-white transition-transform active:scale-95 relative"
            style={{ background: '#7966FF', boxShadow: '0 4px 12px rgba(121,102,255,0.45)' }}
          >
            {/* 견적 건수 뱃지 */}
            {sentQuoteCount > 0 && !isFabOpen && (
              <div className="absolute -top-1 -right-1 min-w-5 h-5 rounded-full flex items-center justify-center px-1 border-2 border-white"
                style={{ background: '#F97316' }}>
                <span className="text-[10px] font-medium text-white">
                  {sentQuoteCount > 99 ? '99+' : sentQuoteCount}
                </span>
              </div>
            )}
            {isFabOpen ? (
              <i className="ri-close-line text-xl"></i>
            ) : (
              <i className="ri-file-list-line text-xl"></i>
            )}
          </button>
        </div>
      </>

    </div>
  );
}